import { useCallback, useEffect, useState } from 'react';
import {
  ExtendedWarrantyLineConfig,
  ExtendedWarrantyOutput,
  ExtendedWarrantyOutputEntry,
} from '../../types';
import { calculatePrice } from '../../handlers/priceHandler';
import { ExtendedWarrantyDuration } from '../ExtendedWarrantyDuration';
import { useFormatPrice } from '../../hooks/useFormatPrice/useFormatPrice';
import { PrimaryTableRow } from '../styledComponents';

type ExtendedWarrantyLineProps = {
  row: number;
  config: ExtendedWarrantyLineConfig;
  currency: string;
  readOnly: boolean;
  configState: ExtendedWarrantyOutputEntry;
  onChange: (newValue: ExtendedWarrantyOutput) => void;
};

type ExtendedWarrantyLineState = {
  warrantyDuration: number;
  warrantyPrice: number;
};

export const ExtendedWarrantyLine = ({
  row,
  currency,
  config,
  configState,
  readOnly,
  onChange,
}: ExtendedWarrantyLineProps) => {
  const formatPrice = useFormatPrice(currency);

  const [warrantyLine, setWarrantyLine] = useState<ExtendedWarrantyLineState>(() => {
    const duration = configState?.warrantyDuration ?? config.warrantyOptions[0].duration;
    let unitPrice = config.warrantyOptions[0].price;

    if (configState?.warrantyDuration) {
      const option = config.warrantyOptions.find(
        (option) => option.duration === configState?.warrantyDuration
      );

      if (option) {
        unitPrice = option.price;
      }
    }

    return {
      warrantyDuration: duration,
      warrantyPrice: calculatePrice({
        warrantedQuantity: config.warrantedQuantity,
        warrantyDuration: duration,
        warrantyPrice: unitPrice,
      }),
    };
  });

  const handleDurationChange = useCallback(
    (newDuration: number) => {
      const selectedOption = config.warrantyOptions.find(
        (option) => option.duration === newDuration
      );

      if (!selectedOption) {
        return;
      }

      const warrantyPrice = calculatePrice({
        warrantedQuantity: config.warrantedQuantity,
        warrantyDuration: selectedOption.duration,
        warrantyPrice: selectedOption.price,
      });

      setWarrantyLine({
        warrantyDuration: newDuration,
        warrantyPrice,
      });

      onChange({
        [config.warrantedPartNumber]: {
          currency,
          warrantedPartNumber: config.warrantedPartNumber,
          warrantedQuantity: config.warrantedQuantity,
          warrantyPartNumber: config.warrantyPartNumber,
          warrantyDuration: selectedOption.duration,
          warrantyPrice,
        },
      });
    },
    [config, currency, onChange]
  );

  useEffect(() => {
    if (warrantyLine.warrantyDuration !== configState.warrantyDuration) {
      handleDurationChange(configState.warrantyDuration);
    }
  }, [warrantyLine.warrantyDuration, configState.warrantyDuration, handleDurationChange]);

  return (
    <PrimaryTableRow>
      <td>{row}</td>
      <td>{config.warrantedPartNumber}</td>
      <td>{config.warrantedPartDescription}</td>
      <td>{config.warrantedQuantity}</td>
      <td>{config.warrantyStdDuration}</td>
      <td>
        <ExtendedWarrantyDuration
          readOnly={readOnly}
          options={config.warrantyOptions}
          onChange={handleDurationChange}
          selectedOption={warrantyLine.warrantyDuration}
        />
      </td>
      <td>{formatPrice(warrantyLine.warrantyPrice)}</td>
    </PrimaryTableRow>
  );
};
